import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild, input, signal, effect, inject } from '@angular/core';
import { ControlValueAccessor, FormsModule, NgControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { NgControlBaseComponent } from 'projects/components/util/ng-control-base.component';
import { CallbackWrapperService } from 'projects/login/src/app/services/callback-wrapper.service';
import { noop } from 'rxjs';
import { FieldMultiValidatorComponent } from '../field-multi-validator/field-multi-validator.component';
import { IconComponent } from '../pay-icon/icon.component';
import { IconNames } from '../pay-icon/IconNames';

@Component({
  selector: 'pay-input',
  imports: [FormsModule, NgClass, FieldMultiValidatorComponent, TranslateModule, ReactiveFormsModule, IconComponent],
  templateUrl: './input.component.html',
  changeDetection: ChangeDetectionStrategy.Default,
})
export class InputComponent extends NgControlBaseComponent implements ControlValueAccessor, OnInit, OnDestroy {
  override ngControl: NgControl;
  override readonly cdRef: ChangeDetectorRef;
  override readonly callbackService: CallbackWrapperService;

  readonly name = input<string>('');
  readonly label = input<string>('');
  readonly internalLabel = input<string>('');
  readonly placeholder = input<string>('');
  readonly required = input<boolean>(false);
  readonly disabled = input<boolean>(false);
  readonly type = input('text');
  readonly helperText = input('');
  readonly overrideValidation = input<boolean>(false);
  readonly autoComplete = input<string>('');
  readonly showPasswordSvg = input<boolean>(false);
  readonly maxLength = input<number>();
  readonly leftAttached = input<boolean>(false);
  readonly textOffset = input<string>('');

  // The higher the level, the more parts of the input get validation highlighting
  readonly validationLevel = input<1 | 2>(1);
  readonly _disabled = signal<boolean>(false);
  readonly _type = signal('text');

  @ViewChild('inputElement') inputElement!: ElementRef<HTMLInputElement>;

  value: string = '';
  showPassword = false;

  protected readonly Validators = Validators;
  protected readonly IconNames = IconNames;

  onChange: (value: string) => void = noop;
  onTouch: (fn: () => void) => void = noop;

  constructor() {
    const ngControl = inject(NgControl);
    const cdRef = inject(ChangeDetectorRef);
    const callbackService = inject(CallbackWrapperService);

    super(cdRef, callbackService, ngControl);
    this.ngControl = ngControl;
    this.cdRef = cdRef;
    this.callbackService = callbackService;

    ngControl.valueAccessor = this;
    effect(() => {
      this._disabled.set(this.disabled());
      this._type.set(this.type());
    });
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouch = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabled.set(isDisabled);
  }

  writeValue(value: string): void {
    const maxLength = this.maxLength();
    if (maxLength && value.length >= maxLength) {
      value = value.substring(0, maxLength);
    }

    this.value = value;
    this.onChange(value);
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
    this._type.set(!this.showPassword ? 'password' : 'text');
  }

  get ringClass() {
    const leftAttached = this.leftAttached();
    if (this.ngControl.touched && this.ngControl.invalid && !leftAttached) {
      if (this.validationLevel() > 1) {
        return 'ring-1 rounded-lg ring-negative-100';
      } else {
        return 'ring-1 rounded-lg ring-neutral-40 focus:ring-negative-100';
      }
    } else if (leftAttached) {
      return 'rounded-bl-lg rounded-tl-lg border-b border-l border-t border-neutral-40 focus:border-b focus:border-l focus:border-r-0 focus:border-t focus:border-primary focus:ring-0 ring-0';
    } else {
      return 'ring-1 rounded-lg ring-neutral-40 focus:ring-2 focus:ring-primary';
    }
  }

  setSelectionRange(inputElement: HTMLInputElement) {
    // selectionRange is only supported for certain input types (text, search, password, tel, url)
    if (['password', 'search', 'tel', 'text', 'url'].includes(inputElement.type.toLowerCase())) {
      inputElement.setSelectionRange(inputElement.value.length, inputElement.value.length);
    }
  }
}
